import React, { useState, useCallback, useEffect, useRef } from 'react';
import { MapPin, Search, CheckCircle, X, Navigation, Globe } from 'lucide-react';
import { t } from '../i18n';

// Ülkeler ve eyalet/şehir bilgileri
const COUNTRIES = {
  'TR': {
    name: 'Türkiye',
    states: [
      'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Amasya', 'Ankara', 'Antalya', 'Artvin',
      'Aydın', 'Balıkesir', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa',
      'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Edirne', 'Elazığ', 'Erzincan',
      'Erzurum', 'Eskişehir', 'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkâri', 'Hatay', 'Isparta',
      'Mersin', 'İstanbul', 'İzmir', 'Kars', 'Kastamonu', 'Kayseri', 'Kırklareli', 'Kırşehir',
      'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 'Kahramanmaraş', 'Mardin', 'Muğla',
      'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Rize', 'Sakarya', 'Samsun', 'Siirt', 'Sinop',
      'Sivas', 'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Şanlıurfa', 'Uşak', 'Van',
      'Yozgat', 'Zonguldak', 'Aksaray', 'Bayburt', 'Karaman', 'Kırıkkale', 'Batman', 'Şırnak',
      'Bartın', 'Ardahan', 'Iğdır', 'Yalova', 'Karabük', 'Kilis', 'Osmaniye', 'Düzce'
    ].sort(),
    stateLabel: 'İl',
    districtLabel: 'İlçe'
  },
  'US': {
    name: 'United States',
    states: [
      'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
      'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
      'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
      'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
      'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
      'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
      'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
      'Wisconsin', 'Wyoming'
    ],
    stateLabel: 'State',
    districtLabel: 'City'
  },
  'DE': {
    name: 'Germany',
    states: [
      'Baden-Württemberg', 'Bayern', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hessen',
      'Mecklenburg-Vorpommern', 'Niedersachsen', 'Nordrhein-Westfalen', 'Rheinland-Pfalz',
      'Saarland', 'Sachsen', 'Sachsen-Anhalt', 'Schleswig-Holstein', 'Thüringen'
    ],
    stateLabel: 'Bundesland',
    districtLabel: 'Stadt'
  },
  'GB': {
    name: 'United Kingdom',
    states: [
      'England', 'Scotland', 'Wales', 'Northern Ireland'
    ],
    stateLabel: 'Country',
    districtLabel: 'City'
  },
  'FR': {
    name: 'France',
    states: [
      'Île-de-France', 'Auvergne-Rhône-Alpes', 'Hauts-de-France', 'Nouvelle-Aquitaine',
      'Occitanie', 'Grand Est', 'Provence-Alpes-Côte d\'Azur', 'Pays de la Loire',
      'Bretagne', 'Normandie', 'Bourgogne-Franche-Comté', 'Centre-Val de Loire',
      'Corse', 'Guadeloupe', 'Martinique', 'Guyane', 'La Réunion', 'Mayotte'
    ],
    stateLabel: 'Région',
    districtLabel: 'Ville'
  },
  'CA': {
    name: 'Canada',
    states: [
      'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
      'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
      'Quebec', 'Saskatchewan', 'Yukon'
    ],
    stateLabel: 'Province',
    districtLabel: 'City'
  },
  'OTHER': {
    name: 'Other Country',
    states: [],
    stateLabel: 'State/Province',
    districtLabel: 'City'
  }
};

const COUNTRY_LIST = [
  { code: 'TR', name: 'Türkiye' },
  { code: 'US', name: 'United States' },
  { code: 'DE', name: 'Germany' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'FR', name: 'France' },
  { code: 'CA', name: 'Canada' },
  { code: 'OTHER', name: 'Other Country' }
];

/**
 * Modern Adres Girişi Komponenti
 * SignupScreen tarzında kullanıcı dostu arayüz
 */
const AddressInput = React.memo(({
  value = {},
  onChange,
  isDarkMode = false,
  error = {},
  label = "Adres Bilgileri"
}) => {
  const [address, setAddress] = useState({
    street: '',
    district: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'TR',
    ...value
  });

  const [showStateSuggestions, setShowStateSuggestions] = useState(false);
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [locationSupported, setLocationSupported] = useState(false);

  const stateInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Konum servisi kontrolü
  useEffect(() => {
    setLocationSupported('geolocation' in navigator);
  }, []);

  // Seçili ülkenin bilgileri
  const selectedCountryData = COUNTRIES[address.country] || COUNTRIES['OTHER'];

  // Eyalet/İl filtreleme
  const filteredStates = stateSearchTerm.length > 0 && selectedCountryData.states.length > 0
    ? selectedCountryData.states.filter(state =>
      state.toLowerCase().includes(stateSearchTerm.toLowerCase())
    ).slice(0, 8)
    : [];

  // Posta kodu formatı kontrolü
  const getPostalCodeFormat = (countryCode) => {
    switch (countryCode) {
      case 'TR': return { placeholder: '06100', maxLength: 5, pattern: /^\d{5}$/ };
      case 'US': return { placeholder: '12345-6789', maxLength: 10, pattern: /^\d{5}(-\d{4})?$/ };
      case 'DE': return { placeholder: '12345', maxLength: 5, pattern: /^\d{5}$/ };
      case 'GB': return { placeholder: 'SW1A 1AA', maxLength: 8, pattern: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i };
      case 'FR': return { placeholder: '75001', maxLength: 5, pattern: /^\d{5}$/ };
      case 'CA': return { placeholder: 'K1A 0A6', maxLength: 7, pattern: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i };
      default: return { placeholder: '12345', maxLength: 10, pattern: null };
    }
  };

  // Adres değişiklik handler'ı
  const handleAddressChange = useCallback((field, newValue) => {
    const updatedAddress = { ...address, [field]: newValue };
    setAddress(updatedAddress);
    onChange && onChange(updatedAddress);
  }, [address, onChange]);
  // Şehir/Eyalet seçimi
  const handleStateSelect = useCallback((selectedState) => {
    handleAddressChange('state', selectedState);
    setStateSearchTerm('');
    setShowStateSuggestions(false);
  }, [handleAddressChange]);

  // Ülke değişimi
  const handleCountryChange = useCallback((newCountry) => {
    const updatedAddress = {
      ...address,
      country: newCountry,
      state: '', // Reset state when country changes
      city: '',  // Reset city when country changes
      postalCode: '' // Reset postal code when country changes
    };
    setAddress(updatedAddress);
    onChange && onChange(updatedAddress);
    setStateSearchTerm('');
    setShowStateSuggestions(false);
  }, [address, onChange]);
  // Konum alma
  const getCurrentLocation = useCallback(async () => {
    if (!locationSupported) return;

    setIsLocating(true);

    try {
      // Enhanced location accuracy with multiple attempts and drift reduction
      const getHighAccuracyPosition = () => {
        return new Promise((resolve, reject) => {
          let attempts = 0;
          const maxAttempts = 3;
          let bestPosition = null;
          let bestAccuracy = Infinity;

          const tryGetPosition = () => {
            attempts++;

            navigator.geolocation.getCurrentPosition(
              (position) => {
                const accuracy = position.coords.accuracy;

                // Keep the most accurate reading
                if (accuracy < bestAccuracy) {
                  bestAccuracy = accuracy;
                  bestPosition = position;
                }

                // If we have good accuracy (< 50m) or exhausted attempts, resolve
                if (accuracy < 50 || attempts >= maxAttempts) {
                  resolve(bestPosition || position);
                } else {
                  // Try again for better accuracy
                  setTimeout(tryGetPosition, 1000);
                }
              },
              (error) => {
                if (attempts >= maxAttempts) {
                  reject(error);
                } else {
                  // Retry with less strict settings
                  setTimeout(tryGetPosition, 1000);
                }
              },
              {
                enableHighAccuracy: true,
                timeout: 8000,
                maximumAge: 30000 // Reduced cache time for fresher data
              }
            );
          };

          tryGetPosition();
        });
      };

      const position = await getHighAccuracyPosition();
      const { latitude, longitude, accuracy, altitude, heading, speed } = position.coords;

      // Enhanced location data with accuracy information
      const locationData = {
        latitude: parseFloat(latitude.toFixed(8)), // Higher precision
        longitude: parseFloat(longitude.toFixed(8)),
        accuracy: Math.round(accuracy),
        timestamp: new Date().toISOString(),
        ...(altitude && { altitude: Math.round(altitude) }),
        ...(heading && { heading: Math.round(heading) }),
        ...(speed && { speed: Math.round(speed * 3.6) }) // Convert m/s to km/h
      };

      const updatedAddress = {
        ...address,
        coordinates: locationData,
        street: `Lat: ${latitude.toFixed(6)}, Lng: ${longitude.toFixed(6)} (±${Math.round(accuracy)}m)`
      };

      setAddress(updatedAddress);
      onChange && onChange(updatedAddress);

      console.log('Location acquired:', {
        accuracy: `±${Math.round(accuracy)}m`,
        coordinates: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
      });

    } catch (error) {
      console.error('Location acquisition failed:', error);

      // Fallback: Try with reduced accuracy requirements
      try {
        const fallbackPosition = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 15000,
            maximumAge: 300000 // 5 minutes cache for fallback
          });
        });

        const { latitude, longitude, accuracy } = fallbackPosition.coords;
        const updatedAddress = {
          ...address,
          coordinates: {
            latitude: parseFloat(latitude.toFixed(6)),
            longitude: parseFloat(longitude.toFixed(6)),
            accuracy: Math.round(accuracy),
            timestamp: new Date().toISOString(),
            fallback: true
          },
          street: `Lat: ${latitude.toFixed(6)}, Lng: ${longitude.toFixed(6)} (Low accuracy: ±${Math.round(accuracy)}m)`
        };

        setAddress(updatedAddress);
        onChange && onChange(updatedAddress);

        console.warn('Using fallback location with reduced accuracy');
      } catch (fallbackError) {
        console.error('Fallback location also failed:', fallbackError);
      }
    } finally {
      setIsLocating(false);
    }
  }, [locationSupported, address, onChange]);
  // Otomatik tamamlama için eyalet/il input değişimi
  const handleStateInputChange = useCallback((e) => {
    const value = e.target.value;
    setStateSearchTerm(value);
    handleAddressChange('state', value);
    setShowStateSuggestions(value.length > 0 && selectedCountryData.states.length > 0);
  }, [handleAddressChange, selectedCountryData.states.length]);

  // Posta kodu formatlama
  const handlePostalCodeChange = useCallback((e) => {
    const value = e.target.value;
    const postalFormat = getPostalCodeFormat(address.country);

    let formattedValue = value;

    // Ülkeye özel formatlar
    if (address.country === 'US') {
      // US: 12345-6789 formatı
      formattedValue = value.replace(/\D/g, '').replace(/(\d{5})(\d{4})/, '$1-$2').slice(0, 10);
    } else if (address.country === 'GB') {
      // UK: SW1A 1AA formatı (basitleştirilmiş)
      formattedValue = value.toUpperCase().slice(0, postalFormat.maxLength);
    } else if (address.country === 'CA') {
      // Canada: K1A 0A6 formatı
      formattedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/(\w{3})(\w{3})/, '$1 $2').slice(0, postalFormat.maxLength);
    } else {
      // Diğer ülkeler için sayısal format
      formattedValue = value.replace(/\D/g, '').slice(0, postalFormat.maxLength);
    }

    handleAddressChange('postalCode', formattedValue);
  }, [address.country, handleAddressChange]);
  // Dış tıklama kontrolü
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowStateSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const postalFormat = getPostalCodeFormat(address.country);

  return (
    <div className="space-y-4">
      {/* Başlık ve Konum Butonu */}
      <div className="flex justify-between items-center">
        <h3 className={`text-lg font-semibold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <MapPin size={20} className="mr-2" />
          {label}
        </h3>

        {locationSupported && (
          <button
            onClick={getCurrentLocation}
            disabled={isLocating}
            className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 ${isDarkMode
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
              } ${isLocating ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Navigation size={16} className={isLocating ? 'animate-spin' : ''} />
            <span className="text-sm font-medium">
              {isLocating ? 'Konumunuz Alınıyor...' : 'Konumumu Al'}
            </span>
          </button>
        )}
      </div>
      {/* Adres Alanları */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Ülke (En üstte) */}
        <div className="md:col-span-2">
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <Globe size={16} className="inline mr-1" />
            Ülke <span className="text-red-500">*</span>
          </label>
          <select
            value={address.country || 'TR'}
            onChange={(e) => handleCountryChange(e.target.value)}
            className={`w-full p-3 rounded-xl transition-all duration-200 border-2 ${error.country
                ? 'border-red-500 focus:border-red-500'
                : 'border-transparent focus:border-blue-500'
              } ${isDarkMode
                ? 'bg-gray-700 text-white'
                : 'bg-gray-100 text-gray-900'
              } focus:outline-none focus:ring-4 focus:ring-blue-500/20`}
          >
            {COUNTRY_LIST.map(country => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
          {error.country && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <X size={12} className="mr-1" />
              {error.country}
            </p>
          )}
        </div>

        {/* Sokak/Cadde */}
        <div className="md:col-span-2">
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {address.country === 'TR' ? 'Sokak/Cadde' : 'Street Address'} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={address.street || ''}
            onChange={(e) => handleAddressChange('street', e.target.value)}
            placeholder={address.country === 'TR' ? 'Örn: Atatürk Caddesi No: 123/A' : 'e.g. 123 Main Street, Apt 4B'}
            className={`w-full p-3 rounded-xl transition-all duration-200 border-2 ${error.street
                ? 'border-red-500 focus:border-red-500'
                : 'border-transparent focus:border-blue-500'
              } ${isDarkMode
                ? 'bg-gray-700 text-white placeholder-gray-400'
                : 'bg-gray-100 text-gray-900 placeholder-gray-500'
              } focus:outline-none focus:ring-4 focus:ring-blue-500/20`}
          />
          {error.street && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <X size={12} className="mr-1" />
              {error.street}
            </p>
          )}
        </div>

        {/* Eyalet/İl (Otomatik Tamamlama) */}
        {selectedCountryData.states.length > 0 && (
          <div className="relative" ref={suggestionsRef}>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {selectedCountryData.stateLabel} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                ref={stateInputRef}
                type="text"
                value={address.state || ''}
                onChange={handleStateInputChange}
                placeholder={`${selectedCountryData.stateLabel} seçin veya yazın`}
                className={`w-full p-3 rounded-xl transition-all duration-200 border-2 ${error.state
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-transparent focus:border-blue-500'
                  } ${isDarkMode
                    ? 'bg-gray-700 text-white placeholder-gray-400'
                    : 'bg-gray-100 text-gray-900 placeholder-gray-500'
                  } focus:outline-none focus:ring-4 focus:ring-blue-500/20`}
              />
              <Search size={16} className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`} />
            </div>

            {/* Eyalet/İl Önerileri */}
            {showStateSuggestions && filteredStates.length > 0 && (
              <div className={`absolute z-10 w-full mt-1 rounded-xl shadow-lg border max-h-48 overflow-y-auto ${isDarkMode
                  ? 'bg-gray-800 border-gray-600'
                  : 'bg-white border-gray-200'
                }`}>
                {filteredStates.map((state, index) => (
                  <button
                    key={state}
                    onClick={() => handleStateSelect(state)}
                    className={`w-full text-left px-4 py-3 hover:bg-blue-500 hover:text-white transition-colors ${index === 0 ? 'rounded-t-xl' : ''
                      } ${index === filteredStates.length - 1 ? 'rounded-b-xl' : ''
                      } ${isDarkMode ? 'text-gray-300 hover:bg-blue-600' : 'text-gray-700'
                      }`}
                  >
                    {state}
                  </button>
                ))}
              </div>
            )}

            {error.state && (
              <p className="text-red-500 text-xs mt-1 flex items-center">
                <X size={12} className="mr-1" />
                {error.state}
              </p>
            )}
          </div>
        )}

        {/* Şehir */}
        <div className={selectedCountryData.states.length > 0 ? '' : 'md:col-span-2'}>
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {selectedCountryData.districtLabel} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={address.city || ''}
            onChange={(e) => handleAddressChange('city', e.target.value)}
            placeholder={address.country === 'TR' ? 'Örn: Ankara' : 'e.g. New York'}
            className={`w-full p-3 rounded-xl transition-all duration-200 border-2 ${error.city
                ? 'border-red-500 focus:border-red-500'
                : 'border-transparent focus:border-blue-500'
              } ${isDarkMode
                ? 'bg-gray-700 text-white placeholder-gray-400'
                : 'bg-gray-100 text-gray-900 placeholder-gray-500'
              } focus:outline-none focus:ring-4 focus:ring-blue-500/20`}
          />
          {error.city && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <X size={12} className="mr-1" />
              {error.city}
            </p>
          )}
        </div>

        {/* Mahalle/İlçe/Bölge */}
        <div>
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {address.country === 'TR' ? 'Mahalle/İlçe' : 'District/Borough'}
          </label>
          <input
            type="text"
            value={address.district || ''}
            onChange={(e) => handleAddressChange('district', e.target.value)}
            placeholder={address.country === 'TR' ? 'Örn: Çankaya' : 'e.g. Manhattan'}
            className={`w-full p-3 rounded-xl transition-all duration-200 border-2 border-transparent focus:border-blue-500 ${isDarkMode
                ? 'bg-gray-700 text-white placeholder-gray-400'
                : 'bg-gray-100 text-gray-900 placeholder-gray-500'
              } focus:outline-none focus:ring-4 focus:ring-blue-500/20`}
          />
        </div>

        {/* Posta Kodu */}
        <div>
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {address.country === 'TR' ? 'Posta Kodu' : 'Postal Code'}
          </label>
          <input
            type="text"
            value={address.postalCode || ''}
            onChange={handlePostalCodeChange}
            placeholder={postalFormat.placeholder}
            maxLength={postalFormat.maxLength}
            className={`w-full p-3 rounded-xl transition-all duration-200 border-2 ${error.postalCode
                ? 'border-red-500 focus:border-red-500'
                : 'border-transparent focus:border-blue-500'
              } ${isDarkMode
                ? 'bg-gray-700 text-white placeholder-gray-400'
                : 'bg-gray-100 text-gray-900 placeholder-gray-500'
              } focus:outline-none focus:ring-4 focus:ring-blue-500/20`}
          />
          {error.postalCode && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <X size={12} className="mr-1" />
              {error.postalCode}
            </p>
          )}
        </div>
      </div>

      {/* Konum Koordinatları (Eğer varsa) */}
      {address.coordinates && (
        <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}>
          <div className="flex items-center text-sm">
            <CheckCircle size={16} className="text-green-500 mr-2" />
            <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
              Konum belirlendi: {address.coordinates.latitude.toFixed(6)}, {address.coordinates.longitude.toFixed(6)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
});

AddressInput.displayName = 'AddressInput';

export default AddressInput;
